import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { api } from '../../services/api';
import { useToast } from '../hooks/useToast';
import { Loader2 } from 'lucide-react';
import { customers } from '../data/dummyData';

const Customers = () => {
  const [customerList, setCustomerList] = useState(customers);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { warning } = useToast();

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Try to fetch from API
        try {
          const response = await api.get('/admin/customers');
          const apiCustomers = (response.data?.data || []).map(customer => ({
            id: customer._id || customer.id,
            name: customer.name || customer.fullName || 'Unknown',
            email: customer.email || '',
            phone: customer.phone || '',
            orders: 0,
            totalSpend: 0,
            status: 'regular',
            joinDate: new Date(customer.createdAt || Date.now()).toLocaleDateString(),
          }));
          setCustomerList(apiCustomers);
        } catch (apiErr) {
          // API endpoint doesn't exist, try alternative
          const ordersResponse = await api.get('/orders');
          const orders = ordersResponse.data?.data || [];
          
          // Extract unique customers from orders
          const customerMap = new Map();
          orders.forEach(order => {
            const customerId = order.customer?._id || order.customerId || order.id;
            if (!customerMap.has(customerId)) {
              customerMap.set(customerId, {
                id: customerId,
                name: order.customer?.name || order.customerName || 'Unknown',
                email: order.customer?.email || order.email || '',
                phone: order.customer?.phone || order.phone || '',
                orders: 0,
                totalSpend: 0,
                status: 'regular',
                joinDate: new Date(order.customer?.createdAt || order.createdAt || Date.now()).toLocaleDateString(),
              });
            }
            const customer = customerMap.get(customerId);
            customer.orders += 1;
            customer.totalSpend += order.totalAmount || order.amount || 0;
            if (customer.totalSpend > 50000) customer.status = 'vip';
          });

          setCustomerList(Array.from(customerMap.values()));
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || 'Failed to load customers';
        setError(msg);
        warning(msg);
      } finally {
        setLoading(false);
      }
    };

    loadCustomers();
  }, [warning]);

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'phone', label: 'Phone', sortable: false },
    { key: 'orders', label: 'Orders', sortable: true },
    {
      key: 'totalSpend',
      label: 'Total Spend',
      render: (val) => `₹${Number(val || 0).toLocaleString()}`,
      sortable: true,
    },
    {
      key: 'status',
      label: 'Status',
      render: (val) => (
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
          val === 'vip'
            ? 'bg-purple-100 text-purple-700'
            : 'bg-blue-100 text-blue-700'
        }`}>
          {String(val || 'regular').toUpperCase()}
        </span>
      ),
    },
    { key: 'joinDate', label: 'Join Date', sortable: true },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customers"
        description="View and manage your customers"
        breadcrumbs={['Home', 'Customers']}
      />

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 flex items-center justify-center">
          <Loader2 className="animate-spin text-amber-600 mr-2" size={20} />
          <span className="text-gray-600">Loading customers...</span>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={customerList}
          searchable={true}
          paginate={true}
          itemsPerPage={10}
        />
      )}
    </div>
  );
};

export default Customers;
